import type { PublicProfileButtonShape, PublicProfileButtonStyle } from '@tabbio/types';

export type PublicProfilePreviewDesignInput = {
  theme: string;
  accent: string;
  buttonStyle: PublicProfileButtonStyle;
  buttonShape: PublicProfileButtonShape;
};

type PreviewTheme = {
  id: string;
  previewBg: string;
  previewCard: string;
  previewText: string;
};

const PREVIEW_THEMES: PreviewTheme[] = [
  { id: 'tabbio', previewBg: '#1E1E1E', previewCard: 'rgba(255,255,255,0.16)', previewText: '#FFFFFF' },
  { id: 'air', previewBg: '#F4F4F4', previewCard: '#FFFFFF', previewText: '#111827' },
  { id: 'midnight', previewBg: '#0E1320', previewCard: '#171B29', previewText: '#FFFFFF' },
  { id: 'galaxy', previewBg: '#1B1033', previewCard: '#291B4A', previewText: '#F8FAFC' },
  { id: 'glass', previewBg: '#7A88A2', previewCard: 'rgba(255,255,255,0.28)', previewText: '#FFFFFF' },
  { id: 'aurora', previewBg: '#1D2842', previewCard: '#233659', previewText: '#F8FAFC' },
  { id: 'mono', previewBg: '#111111', previewCard: '#1A1A1A', previewText: '#F5F5F5' },
  { id: 'sunset', previewBg: '#B74D2E', previewCard: '#CC6443', previewText: '#FFF7ED' },
  { id: 'ocean', previewBg: '#123A5D', previewCard: '#17496E', previewText: '#F8FAFC' },
];

function getPreviewTheme(themeId: string) {
  return PREVIEW_THEMES.find((theme) => theme.id === themeId) ?? PREVIEW_THEMES[1];
}

function toAlphaHex(color: string, alphaHex: string) {
  if (!/^#[0-9a-fA-F]{6}$/.test(color)) return color;
  return `${color}${alphaHex}`;
}

export type ResolvedPublicProfilePreviewDesign = {
  accent: string;
  previewBackground: string;
  previewCard: string;
  previewText: string;
  previewMutedText: string;
  actionButtonRadius: number;
  actionButtonBackground: string;
  actionButtonBorder: string;
  actionButtonText: string;
  actionButtonBorderWidth: number;
  cssVariables: Record<string, string>;
};

export function resolvePublicProfilePreviewDesign(
  input: PublicProfilePreviewDesignInput,
): ResolvedPublicProfilePreviewDesign {
  const selectedTheme = getPreviewTheme(input.theme);
  const previewBackground = selectedTheme.previewBg;
  const previewCard = selectedTheme.previewCard;
  const previewText = selectedTheme.previewText;
  const previewMutedText = previewText === '#111827' ? '#475569' : 'rgba(255,255,255,0.76)';
  const actionButtonRadius =
    input.buttonShape === 'pill' ? 999 : input.buttonShape === 'square' ? 8 : 16;
  const actionButtonBackground =
    input.buttonStyle === 'fill'
      ? input.accent
      : input.buttonStyle === 'soft'
        ? toAlphaHex(input.accent, '22')
        : input.buttonStyle === 'glass'
          ? previewCard
          : 'transparent';
  const actionButtonBorder = input.buttonStyle === 'outline' ? input.accent : 'transparent';
  const actionButtonText = input.buttonStyle === 'fill' ? '#FFFFFF' : previewText;
  const actionButtonBorderWidth = input.buttonStyle === 'outline' ? 1 : 0;

  return {
    accent: input.accent,
    previewBackground,
    previewCard,
    previewText,
    previewMutedText,
    actionButtonRadius,
    actionButtonBackground,
    actionButtonBorder,
    actionButtonText,
    actionButtonBorderWidth,
    cssVariables: {
      '--tabbio-surface': previewBackground,
      '--tabbio-surface-secondary': previewBackground,
      '--tabbio-surface-elevated': previewCard,
      '--tabbio-text': previewText,
      '--tabbio-text-secondary': previewMutedText,
      '--tabbio-text-tertiary': previewMutedText,
      '--tabbio-border-subtle': toAlphaHex(previewText, '14'),
      '--tabbio-border': toAlphaHex(previewText, '24'),
      '--tabbio-button-primary-bg': actionButtonBackground,
      '--tabbio-button-primary-text': actionButtonText,
      '--tabbio-button-primary-border': actionButtonBorder,
      '--tabbio-preview-action-radius': `${actionButtonRadius}px`,
      '--tabbio-preview-action-border-width': `${actionButtonBorderWidth}px`,
    },
  };
}
